/**
 * Income Statement API Service
 */

import { axiosInterceptor } from '../../../services'
import type {
	IncomeStatement,
	LineItem,
	ApiResponse,
	SaveResponse,
} from '../types/financials.types'

const API_BASE_URL = '/api'
const api = axiosInterceptor(API_BASE_URL)

/**
 * Get income statement for a location
 */
export const getIncomeStatement = async (locationId: string): Promise<IncomeStatement | null> => {
	try {
		const response = await api.get<ApiResponse<IncomeStatement>>(
			`/locations/${locationId}/income-statement`,
		)

		if (response.data.success && response.data.data) {
			return response.data.data
		}

		return null
	} catch (error: any) {
		if (error.response?.status === 404) {
			// No income statement exists yet
			return null
		}
		throw error
	}
}

/**
 * Create a new income statement
 */
export const createIncomeStatement = async (
	locationId: string,
	incomeStatement: Partial<IncomeStatement>,
): Promise<IncomeStatement> => {
	const response = await api.post<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement`,
		incomeStatement,
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to create income statement')
	}

	return response.data.data
}

/**
 * Update an existing income statement
 */
export const updateIncomeStatement = async (
	locationId: string,
	incomeStatement: IncomeStatement,
): Promise<SaveResponse> => {
	const response = await api.put<ApiResponse<SaveResponse>>(
		`/locations/${locationId}/income-statement`,
		incomeStatement,
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to update income statement')
	}

	return response.data.data
}

/**
 * Update a single cell value
 */
export const updateCell = async (
	locationId: string,
	lineItemId: string,
	quarterId: string,
	value: number,
): Promise<SaveResponse> => {
	const response = await api.patch<ApiResponse<SaveResponse>>(
		`/locations/${locationId}/income-statement/cells`,
		{
			lineItemId,
			quarterId,
			value,
		},
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to update cell')
	}

	return response.data.data
}

/**
 * Add a line item
 */
export const addLineItem = async (
	locationId: string,
	lineItem: Partial<LineItem>,
): Promise<IncomeStatement> => {
	const response = await api.post<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement/line-items`,
		lineItem,
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to add line item')
	}

	return response.data.data
}

/**
 * Remove a line item
 */
export const removeLineItem = async (
	locationId: string,
	lineItemId: string,
): Promise<IncomeStatement> => {
	const response = await api.delete<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement/line-items/${lineItemId}`,
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to remove line item')
	}

	return response.data.data
}

/**
 * Reorder line items
 */
export const reorderLineItems = async (
	locationId: string,
	lineItemIds: string[],
): Promise<IncomeStatement> => {
	const response = await api.put<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement/line-items/reorder`,
		{ lineItemIds },
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to reorder line items')
	}

	return response.data.data
}

/**
 * Add a quarter to an income statement
 */
export const addQuarter = async (
	locationId: string,
	quarter: { id: string; label: string; order: number },
): Promise<IncomeStatement> => {
	const response = await api.post<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement/quarters`,
		{ quarter },
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to add quarter')
	}

	return response.data.data
}

/**
 * Delete a quarter from an income statement
 */
export const deleteQuarter = async (
	locationId: string,
	quarterId: string,
): Promise<IncomeStatement> => {
	const response = await api.delete<ApiResponse<IncomeStatement>>(
		`/locations/${locationId}/income-statement/quarters/${quarterId}`,
	)

	if (!response.data.success || !response.data.data) {
		throw new Error(response.data.error?.message || 'Failed to delete quarter')
	}

	return response.data.data
}

/**
 * Delete income statement
 */
export const deleteIncomeStatement = async (locationId: string): Promise<void> => {
	const response = await api.delete<ApiResponse<void>>(`/locations/${locationId}/income-statement`)

	if (!response.data.success) {
		throw new Error(response.data.error?.message || 'Failed to delete income statement')
	}
}
